import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  SimpleGrid,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { toast } from 'sonner';
import { InputForm } from '../../../../../../components/inputs/forms/InputForm';
import { TextAreaInputForm } from '../../../../../../components/inputs/forms/TextAreaInputForm';
import { CurrencyInputForm } from '../../../../../../components/inputs/forms/CurrencyInputForm';
import { DateInputForm } from '../../../../../../components/inputs/forms/DateInputForm';
import { useRegimesEspeciais } from '../../../../../../hooks/useRegimesEspeciais';
import {
  EsferaRegime,
  IRegimeEspecial,
  IRegimeEspecialFormPayload,
  StatusRegime,
} from '../../../../../../types/regimeEspecial';

interface RegimeEspecialFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresaId: number;
  regime: IRegimeEspecial | null;
}

interface FormValues {
  esfera: EsferaRegime;
  tipo: string;
  descricao: string;
  numero_ato: string;
  emissor: string;
  base_legal: string;
  dt_inicio: Date | null;
  dt_fim: Date | null;
  economia_estimada: number | null;
  status: StatusRegime;
  observacoes: string;
  uf: string;
  municipio: string;
  municipio_ibge: string;
}

const schema = yup.object({
  esfera: yup.mixed<EsferaRegime>().oneOf(['federal', 'estadual', 'municipal']).required(),
  tipo: yup.string().required('Tipo obrigatório'),
  descricao: yup.string(),
  numero_ato: yup.string(),
  emissor: yup.string(),
  base_legal: yup.string(),
  dt_inicio: yup.date().nullable(),
  dt_fim: yup.date().nullable(),
  economia_estimada: yup.number().nullable(),
  status: yup.mixed<StatusRegime>().oneOf(['ativo', 'suspenso', 'encerrado']).required(),
  observacoes: yup.string(),
  uf: yup.string(),
  municipio: yup.string(),
  municipio_ibge: yup.string(),
});

export function RegimeEspecialFormModal({
  isOpen,
  onClose,
  empresaId,
  regime,
}: RegimeEspecialFormModalProps) {
  const queryClient = useQueryClient();
  const { createRegimeEspecial, updateRegimeEspecial } = useRegimesEspeciais();

  const isEdit = !!regime;

  const methods = useForm<FormValues>({
    resolver: yupResolver<FormValues>(schema as yup.ObjectSchema<FormValues>),
    defaultValues: {
      esfera: 'estadual',
      tipo: '',
      descricao: '',
      numero_ato: '',
      emissor: '',
      base_legal: '',
      dt_inicio: null,
      dt_fim: null,
      economia_estimada: null,
      status: 'ativo',
      observacoes: '',
      uf: '',
      municipio: '',
      municipio_ibge: '',
    },
  });

  const esferaWatch = methods.watch('esfera');

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        esfera: regime?.esfera ?? 'estadual',
        tipo: regime?.tipo ?? '',
        descricao: regime?.descricao ?? '',
        numero_ato: regime?.numero_ato ?? '',
        emissor: regime?.emissor ?? '',
        base_legal: regime?.base_legal ?? '',
        dt_inicio: regime?.dt_inicio ? moment(regime.dt_inicio).toDate() : null,
        dt_fim: regime?.dt_fim ? moment(regime.dt_fim).toDate() : null,
        economia_estimada:
          regime?.economia_estimada !== undefined && regime?.economia_estimada !== null
            ? Number(regime.economia_estimada)
            : null,
        status: regime?.status ?? 'ativo',
        observacoes: regime?.observacoes ?? '',
        uf: regime?.uf ?? '',
        municipio: regime?.municipio ?? '',
        municipio_ibge: regime?.municipio_ibge ?? '',
      });
    }
  }, [isOpen, regime, methods]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: IRegimeEspecialFormPayload = {
        esfera: values.esfera,
        tipo: values.tipo,
        descricao: values.descricao || undefined,
        numero_ato: values.numero_ato || undefined,
        emissor: values.emissor || undefined,
        base_legal: values.base_legal || undefined,
        dt_inicio: values.dt_inicio ? moment(values.dt_inicio).format('YYYY-MM-DD') : null,
        dt_fim: values.dt_fim ? moment(values.dt_fim).format('YYYY-MM-DD') : null,
        economia_estimada: values.economia_estimada,
        status: values.status,
        observacoes: values.observacoes || undefined,
        uf: values.uf || null,
        municipio: values.municipio || null,
        municipio_ibge: values.municipio_ibge || null,
      };
      if (isEdit && regime) {
        return updateRegimeEspecial(empresaId, regime.id, payload);
      }
      return createRegimeEspecial(empresaId, payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Regime atualizado' : 'Regime criado');
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais'] });
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais-empresa'] });
      queryClient.invalidateQueries({ queryKey: ['regimes-especiais-kpis'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar. Tente novamente.');
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEdit ? 'Editar regime especial' : 'Novo regime especial'}</ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(values => mutation.mutate(values))}>
            <ModalBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel fontWeight="bold" mb={1}>
                    Esfera
                  </FormLabel>
                  <select
                    {...methods.register('esfera')}
                    style={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      padding: '0 12px',
                      background: '#fff',
                    }}
                  >
                    <option value="federal">Federal</option>
                    <option value="estadual">Estadual</option>
                    <option value="municipal">Municipal</option>
                  </select>
                </FormControl>
                <FormControl>
                  <FormLabel fontWeight="bold" mb={1}>
                    Status
                  </FormLabel>
                  <select
                    {...methods.register('status')}
                    style={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '6px',
                      border: '1px solid #E2E8F0',
                      padding: '0 12px',
                      background: '#fff',
                    }}
                  >
                    <option value="ativo">Ativo</option>
                    <option value="suspenso">Suspenso</option>
                    <option value="encerrado">Encerrado</option>
                  </select>
                </FormControl>
                <InputForm name="tipo" label="Tipo" placeholder="tare, drawback, etc." isRequired />
                <InputForm name="numero_ato" label="Número do ato" />
                <InputForm name="emissor" label="Emissor" placeholder="SEFAZ-MG, RFB, ..." />
                <InputForm name="base_legal" label="Base legal" />
                {esferaWatch !== 'federal' && (
                  <>
                    <InputForm name="uf" label="UF" placeholder="MG" mask="aa" />
                    {esferaWatch === 'municipal' && (
                      <>
                        <InputForm name="municipio" label="Município" />
                        <InputForm name="municipio_ibge" label="Código IBGE" />
                      </>
                    )}
                  </>
                )}
                <DateInputForm name="dt_inicio" label="Início da vigência" isClearable />
                <DateInputForm name="dt_fim" label="Fim da vigência" isClearable />
                <CurrencyInputForm name="economia_estimada" label="Economia estimada" />
              </SimpleGrid>
              <TextAreaInputForm name="descricao" label="Descrição" />
              <TextAreaInputForm name="observacoes" label="Observações" />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancelar
              </Button>
              <Button colorScheme="blue" type="submit" isLoading={mutation.isPending}>
                Salvar
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}
